import * as React from "react";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";

import { SlimSecondaryButton } from "components/Button";
import {
  eNvdCandidateDecMap,
  getCandidateDeclarationAnswers,
  scannedNvdSuggestedValueMap,
} from "components/AttachmentForm/constants";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

import { CandidateDeclarationSource } from "constants/candidateDeclarations";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

import {
  useFieldState,
  useFieldValue,
  useHasSaleyardOrDeploymentPermission,
} from "hooks";

import { someHasPermission } from "lib/permissions";

import {
  getPropertyById,
  getSaleyards,
  selectConsignmentOptionsByDeploymentSaleId,
} from "selectors";
import {
  getCandidateDeclarationById,
  getSuggestedConsignmentsByCandidateDeclarationId,
  SuggestedConsignmentMatch,
} from "selectors/candidateDeclarations";
import { Column, Row } from "components/Layout";

export function useCandidateDecSourcePermission(source) {
  const hasOcrCandidateDecFeaturePermission =
    useHasSaleyardOrDeploymentPermission(
      DeploymentPermission.featureOcrCandidateDeclarations,
      SaleyardPermission.featureOcrCandidateDeclarations,
    );
  const saleyards = useSelector(getSaleyards);

  const hasEnvdFeaturePermission = someHasPermission(
    Object.values(saleyards),
    SaleyardPermission.featureEnvds,
  );

  if (source === CandidateDeclarationSource.ENVD) {
    return hasEnvdFeaturePermission;
  }

  if (source === CandidateDeclarationSource.CONSIGNMENT_ATTACHMENT_OCR) {
    return hasOcrCandidateDecFeaturePermission;
  }
}

function formatSuggestedLabel(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null) return "Not Answered";
  return String(value);
}

function Suggestion({ onClickUseSuggestion, label }) {
  return (
    <SlimSecondaryButton
      onClick={onClickUseSuggestion}
      type="button"
      verticalMargin={2}
    >
      <FontAwesomeIcon icon={faPlus} />
      &nbsp; {label}
    </SlimSecondaryButton>
  );
}

function ManagedSuggestion({
  suggestedValue,
  label,
  fieldName,
  onSelect = undefined,
}) {
  const [existingValue, setValue] = useFieldState(fieldName);

  if (suggestedValue === undefined || suggestedValue === existingValue) {
    return null;
  }

  return (
    <Suggestion
      onClickUseSuggestion={() => {
        setValue(suggestedValue);
        onSelect?.(suggestedValue);
      }}
      label={label}
    />
  );
}

type ConsignmentOption = { value: string; label: string };

function getWrongDeploymentTooltipMessage(
  agencyName: string,
  consignmentTitle: string,
) {
  return `${consignmentTitle} belongs to ${agencyName}. Upload this document to ${agencyName} to select it.`;
}

function SuggestedConsignmentRow({
  match,
  fieldName,
  label,
  onSelect,
}: {
  match: SuggestedConsignmentMatch;
  fieldName: string;
  label: string;
  onSelect: (value: string) => void;
}) {
  const { wrongDeployment } = match.caveats;

  if (wrongDeployment) {
    return (
      <Row alignEnd>
        <Tooltip
          title={getWrongDeploymentTooltipMessage(match.agencyName, label)}
        >
          <SlimSecondaryButton type="button" disabled verticalMargin={2}>
            <FontAwesomeIcon icon={faPlus} />
            &nbsp;({match.agencyName}) {label}
          </SlimSecondaryButton>
        </Tooltip>
      </Row>
    );
  }

  return (
    <ManagedSuggestion
      label={label}
      fieldName={fieldName}
      suggestedValue={match.consignmentId}
      onSelect={onSelect}
    />
  );
}

function VendorSuggestion({ candidateDeclarationId, fieldName, onSelect }) {
  const matches: SuggestedConsignmentMatch[] = useSelector(
    getSuggestedConsignmentsByCandidateDeclarationId(candidateDeclarationId),
  );
  const consignmentOptionsLookup = useSelector(
    selectConsignmentOptionsByDeploymentSaleId,
  );

  const allConsignmentOptions = Object.values<Array<ConsignmentOption>>(
    consignmentOptionsLookup,
  ).flat();

  if (!matches.length) {
    return null;
  }

  return (
    <Column>
      {matches.map(match => {
        const label = allConsignmentOptions.find(
          option => option.value === match.consignmentId,
        )?.label;

        if (!label) {
          return null;
        }

        return (
          <SuggestedConsignmentRow
            key={match.consignmentId}
            match={match}
            fieldName={fieldName}
            label={label}
            onSelect={onSelect}
          />
        );
      })}
    </Column>
  );
}

function VendorPropertySuggestion({ candidateDeclarationId, fieldName }) {
  const candidateDeclaration = useSelector(
    getCandidateDeclarationById(candidateDeclarationId),
  );
  const property = useSelector(
    getPropertyById(candidateDeclaration?.originPropertyId),
  );

  if (!property) {
    return null;
  }

  return (
    <ManagedSuggestion
      label={property.PIC}
      fieldName={fieldName}
      suggestedValue={candidateDeclaration?.originPropertyId}
    />
  );
}

const buildAdditionalInfoFromProperty = property =>
  [
    property?.msaNumber && `MSA: ${property.msaNumber}`,
    property?.lpaNumber && `LPA: ${property.lpaNumber}`,
  ]
    .filter(Boolean)
    .join(" ");

function AdditionalInformationSuggestion({
  candidateDeclarationId,
  fieldName,
}) {
  const candidateDeclaration = useSelector(
    getCandidateDeclarationById(candidateDeclarationId),
  );
  const vendorPropertyId = useFieldValue("vendor_property_id");
  const vendorProperty = useSelector(getPropertyById(vendorPropertyId));

  const eNvdAdditionalInfo = getCandidateDeclarationAnswers(
    candidateDeclaration,
  ).find(a => a.questionId === "151")?.value;
  const suggestedValue =
    [eNvdAdditionalInfo, buildAdditionalInfoFromProperty(vendorProperty)]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <ManagedSuggestion
      fieldName={fieldName}
      suggestedValue={suggestedValue}
      label={suggestedValue}
    />
  );
}

function GenericOcrSuggestion({ candidateDeclarationId, fieldName }) {
  const candidateDeclaration = useSelector(
    getCandidateDeclarationById(candidateDeclarationId),
  );
  const getter = scannedNvdSuggestedValueMap[fieldName];
  const suggestedValue = getter ? getter(candidateDeclaration) : undefined;
  return (
    <ManagedSuggestion
      fieldName={fieldName}
      suggestedValue={suggestedValue}
      label={formatSuggestedLabel(suggestedValue)}
    />
  );
}

function NvdSuggestion({ candidateDeclarationId, fieldName }) {
  const candidateDeclaration = useSelector(
    getCandidateDeclarationById(candidateDeclarationId),
  );
  if (!candidateDeclaration.serialNumber) {
    return null;
  }
  return (
    <ManagedSuggestion
      fieldName={fieldName}
      suggestedValue={candidateDeclaration.serialNumber}
      label={candidateDeclaration.serialNumber}
    />
  );
}

function EnvdQaSuggestion({ candidateDeclarationId, fieldName }) {
  const candidateDeclaration = useSelector(
    getCandidateDeclarationById(candidateDeclarationId),
  );
  const getter = eNvdCandidateDecMap[fieldName];
  const suggestedValue = getter ? getter(candidateDeclaration) : undefined;
  return (
    <ManagedSuggestion
      fieldName={fieldName}
      suggestedValue={suggestedValue}
      label={formatSuggestedLabel(suggestedValue)}
    />
  );
}

function OcrSuggestion({ candidateDeclarationId, fieldName, onSelect }) {
  if (fieldName === "vendor_property_id") {
    return (
      <VendorPropertySuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
      />
    );
  }
  if (fieldName === "NVD") {
    return (
      <NvdSuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
      />
    );
  }
  if (fieldName === "id") {
    return (
      <VendorSuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
        onSelect={onSelect}
      />
    );
  }
  if (fieldName === "declaration.additional_information") {
    return (
      <AdditionalInformationSuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
      />
    );
  }
  return (
    <GenericOcrSuggestion
      candidateDeclarationId={candidateDeclarationId}
      fieldName={fieldName}
    />
  );
}

function EnvdSuggestion({ candidateDeclarationId, fieldName, onSelect }) {
  if (fieldName === "vendor_property_id") {
    return (
      <VendorPropertySuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
      />
    );
  }
  if (fieldName === "id") {
    return (
      <VendorSuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
        onSelect={onSelect}
      />
    );
  }
  if (fieldName === "declaration.additional_information") {
    return (
      <AdditionalInformationSuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
      />
    );
  }
  return (
    <EnvdQaSuggestion
      candidateDeclarationId={candidateDeclarationId}
      fieldName={fieldName}
    />
  );
}

export function CandidateDeclarationSuggestion({
  candidateDeclarationId,
  fieldName,
  onSelect,
}) {
  const candidateDeclaration = useSelector(
    getCandidateDeclarationById(candidateDeclarationId),
  );
  const hasFeaturePermission = useCandidateDecSourcePermission(
    candidateDeclaration?.source,
  );

  if (!candidateDeclaration || !hasFeaturePermission) {
    return null;
  }

  if (
    candidateDeclaration.source ===
    CandidateDeclarationSource.CONSIGNMENT_ATTACHMENT_OCR
  ) {
    return (
      <OcrSuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
        onSelect={onSelect}
      />
    );
  }
  if (candidateDeclaration.source === CandidateDeclarationSource.ENVD) {
    return (
      <EnvdSuggestion
        candidateDeclarationId={candidateDeclarationId}
        fieldName={fieldName}
        onSelect={onSelect}
      />
    );
  }
  return null;
}
